import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiFailure, ApiResult } from '../../api.js';

// Pages after the first of a list the server serves latest first behind a cursor (`nextBefore`): the executive module's
// earlier reports and its trend (ADR 0019; `GET /api/executive/reports` and the pulse's `trend`, at most 100 a page).
//
// Why `loadAll`: such a list has no server sort, and 04-data §1.2.1 says "Sorting MUST reorder the whole result set, not
// the current page" — so when the reader sorts by anything but the list's own order, the pages that remain are loaded
// first and the sort covers every row (what the retention keeps, a year of reports at most). The first page alone is
// read when the section is shown, and later pages only when the reader asks for them, by "Load more" or by a sort.

export interface CursorPage<Row> {
  items: Row[];
  nextBefore: string | null;
}

export interface CursorPages<Row> {
  rows: Row[];
  /** Whether rows remain to be loaded. */
  more: boolean;
  loading: boolean;
  error: ApiFailure | null;
  loadMore: () => void;
  loadAll: () => void;
}

export function useCursorPages<Row extends { id: string }>(first: CursorPage<Row> | null, fetchPage: (before: string) => Promise<ApiResult<CursorPage<Row>>>): CursorPages<Row> {
  const [later, setLater] = useState<CursorPage<Row> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  // Why a generation: a first page that changes (a reload) discards what was appended to the old one, and a load still in
  // flight for the old one must not land on the new.
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setLater(null);
    setError(null);
    setLoading(false);
  }, [first]);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const all: Row[] = [];
    for (const row of [...(first?.items ?? []), ...(later?.items ?? [])]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      all.push(row);
    }
    return all;
  }, [first, later]);
  const cursor = later ? later.nextBefore : first?.nextBefore ?? null;

  const load = useCallback(async (all: boolean): Promise<void> => {
    if (loading || cursor === null) return;
    const mine = generation.current;
    setLoading(true);
    setError(null);
    let before: string | null = cursor;
    const gathered: Row[] = [];
    while (before !== null) {
      const result = await fetchRef.current(before);
      if (generation.current !== mine) return;
      if (!result.ok) {
        setError(result);
        break;
      }
      gathered.push(...result.data.items);
      before = result.data.nextBefore;
      if (!all) break;
    }
    if (generation.current !== mine) return;
    setLater((current) => ({ items: [...(current?.items ?? []), ...gathered], nextBefore: before }));
    setLoading(false);
  }, [loading, cursor]);

  return { rows, more: cursor !== null, loading, error, loadMore: () => void load(false), loadAll: () => void load(true) };
}
